import { Box, Fade, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { isNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { LazyMountOnVisible } from '../../../../components/utility/LazyMountOnVisible';
import {
  REPORT_CARD_SX,
  REPORT_CHART_AXIS_LABEL_STYLE,
  REPORT_CHART_COLORS,
  REPORT_CHART_TICK_LABEL_STYLE,
} from '../chart-theme';
import { ReportChartLegend } from '../ReportChartLegend';
import { ReportChartTooltipFrame, ReportChartTooltipRow } from '../ReportChartTooltip';
import { useRetentionChartData } from './use-retention-chart-data';

type RetentionTrendChartProps = {
  fromDate: Date | null;
  toDate: Date | null;
  filters?: FilterLineType[];
};

const CHART_HEIGHT = 320;
const CHART_MARGIN = { left: 65, top: 20, right: 20, bottom: 30 } as const;
const NRR_COLOR = REPORT_CHART_COLORS.endingMrr;
const GRR_COLOR = REPORT_CHART_COLORS.new;

const pctFormatter = (value: number | null) => (value == null ? '' : `${value}%`);

export const RetentionTrendChart: React.FC<RetentionTrendChartProps> = ({
  fromDate,
  toDate,
  filters,
}) => {
  const data = useRetentionChartData(fromDate, toDate, filters);
  const isEmpty = data.length === 0;

  const legendItems = useMemo(
    () => [
      { label: 'NRR %', color: NRR_COLOR },
      { label: 'GRR %', color: GRR_COLOR },
    ],
    [],
  );

  const { yMin, yMax } = useMemo(() => {
    if (isEmpty) return { yMin: 0, yMax: 130 };
    const values = data.flatMap((d) => [d.nrr, d.grr]);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return {
      yMin: Math.max(0, Math.floor((min - 5) / 5) * 5),
      yMax: Math.ceil((max + 5) / 5) * 5,
    };
  }, [data, isEmpty]);

  const xAxis = useMemo(
    () => [
      {
        scaleType: 'band' as const,
        dataKey: 'month',
        tickLabelStyle: REPORT_CHART_TICK_LABEL_STYLE,
      },
    ],
    [],
  );

  const yAxis = useMemo(
    () => [
      {
        min: yMin,
        max: yMax,
        valueFormatter: (value: number) => `${value}%`,
        tickLabelStyle: REPORT_CHART_AXIS_LABEL_STYLE,
      },
    ],
    [yMin, yMax],
  );

  const series = useMemo(
    () => [
      { id: 'nrr', dataKey: 'nrr', label: 'NRR %', color: NRR_COLOR, valueFormatter: pctFormatter },
      { id: 'grr', dataKey: 'grr', label: 'GRR %', color: GRR_COLOR, valueFormatter: pctFormatter },
    ],
    [],
  );

  return (
    <Box
      sx={{
        px: 2,
        pb: 2
      }}>
      <Fade in timeout={400}>
        <Box sx={REPORT_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2
            }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Net & Gross Revenue Retention
            </Typography>
          </Box>
          {isEmpty ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 150
              }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                No retention data for the selected period
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: '56px'
              }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0
                }}>
                <LazyMountOnVisible minHeight={CHART_HEIGHT}>
                  <BarChart
                    height={CHART_HEIGHT}
                    margin={CHART_MARGIN}
                    dataset={data}
                    xAxis={xAxis}
                    yAxis={yAxis}
                    series={series}
                    borderRadius={4}
                    grid={{ horizontal: true }}
                    hideLegend
                    slots={{ tooltip: RetentionTrendChartTooltip }}
                    skipAnimation
                  />
                </LazyMountOnVisible>
              </Box>
              <Box sx={{ pt: 1 }}>
                <ReportChartLegend items={legendItems} orientation="vertical" />
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
};

const RetentionTrendChartTooltip: React.FC = () => {
  const tooltipData = useAxesTooltip()?.[0];

  const fmt = (raw: unknown) => (typeof raw === 'number' ? `${raw.toFixed(1)}%` : '--');

  return (
    <ChartsTooltipContainer trigger="axis">
      {!isNullOrUndefined(tooltipData) && (
        <ReportChartTooltipFrame title={String(tooltipData.axisValue)}>
          <ReportChartTooltipRow
            color={NRR_COLOR}
            label="NRR %"
            formattedValue={fmt(tooltipData.seriesItems.find((s) => s.seriesId === 'nrr')?.value)}
          />
          <ReportChartTooltipRow
            color={GRR_COLOR}
            label="GRR %"
            formattedValue={fmt(tooltipData.seriesItems.find((s) => s.seriesId === 'grr')?.value)}
          />
        </ReportChartTooltipFrame>
      )}
    </ChartsTooltipContainer>
  );
};
